import React, { useCallback, useMemo, useState } from "react";
import { HStack } from "@design-system/HStack/HStack";
import { Counterparty } from "@common/types/counterparty";
import { pluralizeWithCount } from "@common/helpers/string";
import { ArrowCornerIcon } from "@icons";
import { Radio, RadioGroup } from "@design-system/Radio/Radio";
import { ModalComponentProps } from "@helpers/modal";
import { CounterpartyCombobox } from "@components/transactions/TransactionSplitV2/CounterpartyCombobox";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import {
    isCounterpartyBillOrInvoiceLinksError,
    isCounterpartyLockedTransactionsError,
    useCounterpartyMergingMutation,
    useCounterpartyRemovalMutation,
} from "@api/counterparty.api";
import { Button } from "@design-system/Button/Button";
import {
    StandardModalBody,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { CounterpartyLinkedToBillsOrInvoicesModal } from "./CounterpartyLinkedToBillsOrInvoicesModal";
import { DisableCounterpartyInsteadModal } from "./DisableCounterpartyInsteadModal";
import { useDirectoryGlDrillDownUrl } from "./useDirectoryGlLink";
import styles from "./MergeOrRemoveCounterpartyModal.module.scss";

interface Props extends ModalComponentProps {
    counterparty: Counterparty;
    journalEntryLinesCount?: number;
}

type CounterpartyAction = "merge" | "remove";

export const MergeOrRemoveCounterpartyModal: React.FC<Props> = ({
    counterparty,
    close,
    journalEntryLinesCount,
}) => {
    const glDrillDownUrl = useDirectoryGlDrillDownUrl(counterparty.id, false);
    const [action, setAction] = useState<CounterpartyAction>();
    const [counterpartyToMergeWith, setCounterpartyToMergeWith] =
        useState<Pick<Counterparty, "id" | "name">>();
    const [showDisableInsteadModal, setShowDisableInsteadModal] =
        useState(false);
    const [showBillOrInvoiceLinksModal, setShowBillOrInvoiceLinksModal] =
        useState(false);
    const submitAllowed =
        (!!counterpartyToMergeWith && action === "merge") ||
        action === "remove";
    const excludeIds = useMemo(() => [counterparty.id], [counterparty.id]);
    const removal = useCounterpartyRemovalMutation(counterparty);
    const merging = useCounterpartyMergingMutation();

    const performAction = useCallback(
        async ({
            shouldDisableInstead,
        }: {
            shouldDisableInstead?: boolean;
        }) => {
            if (action === "remove") {
                await removal.mutateAsync({
                    force: true,
                    disableInsteadOfDelete: shouldDisableInstead,
                });
            } else if (action === "merge" && counterpartyToMergeWith) {
                await merging.mutateAsync({
                    counterpartyToMergeId: counterparty.id,
                    counterpartyToKeepId: counterpartyToMergeWith.id,
                    disableCounterpartyToMerge: shouldDisableInstead,
                });
            } else {
                return;
            }
            close();
        },
        [
            removal,
            merging,
            counterpartyToMergeWith,
            counterparty.id,
            close,
            action,
        ],
    );

    const onSubmit = useCallback(async () => {
        try {
            await performAction({});
        } catch (e) {
            if (
                isCounterpartyLockedTransactionsError(e) &&
                counterparty.workspaceId
            ) {
                setShowDisableInsteadModal(true);
            } else if (
                isCounterpartyBillOrInvoiceLinksError(e) &&
                counterparty.workspaceId
            ) {
                setShowBillOrInvoiceLinksModal(true);
            } else {
                throw e;
            }
        }
    }, [performAction, counterparty.workspaceId]);

    const onDisableInstead = useCallback(async () => {
        await performAction({ shouldDisableInstead: true });
        setShowDisableInsteadModal(false);
    }, [performAction]);

    const onArchiveInstead = useCallback(async () => {
        await performAction({ shouldDisableInstead: true });
        setShowBillOrInvoiceLinksModal(false);
    }, [performAction]);

    return (
        <>
            <StandardModalHeader>
                Delete {counterparty.name}
            </StandardModalHeader>
            <StandardModalBody>
                <p>
                    <strong className="font-weight-medium">
                        {counterparty.name}
                    </strong>{" "}
                    is assigned to{" "}
                    <a href={glDrillDownUrl}>
                        {pluralizeWithCount(
                            "journal entry line",
                            journalEntryLinesCount ?? 0,
                        )}
                        <ArrowCornerIcon className="ml-1 icon-size-text" />
                    </a>
                    . Would you like to re-assign these journal entries to a
                    different counterparty?
                </p>

                <form>
                    <RadioGroup<CounterpartyAction | "">
                        name="action"
                        aria-label="Delete counterparty action"
                        value={action ?? ""}
                        onValueChange={(value) => {
                            if (value !== "") {
                                setAction(value);
                            }
                        }}
                        className={styles.radioGroup}
                    >
                        <Radio
                            value="remove"
                            label="No, leave journal entries without counterparty"
                            className={styles.radio}
                        />
                        <Radio
                            value="merge"
                            label="Yes, select different counterparty"
                            className={styles.radio}
                        />
                    </RadioGroup>

                    {action === "merge" && (
                        <CounterpartyCombobox
                            selectedCounterparty={counterpartyToMergeWith}
                            onChange={setCounterpartyToMergeWith}
                            placeholder="Select counterparty..."
                            hideFooter
                            excludeIds={excludeIds}
                        />
                    )}

                    <HStack as="footer" justify="between" className="mt-5">
                        <Button
                            variant="secondary"
                            size="md"
                            type="button"
                            onClick={close}
                        >
                            Cancel
                        </Button>
                        <ButtonWithLoader
                            variant="primary"
                            size="md"
                            type="button"
                            loading={removal.isPending || merging.isPending}
                            disabled={!submitAllowed}
                            onClick={onSubmit}
                        >
                            Delete
                        </ButtonWithLoader>
                    </HStack>
                </form>
            </StandardModalBody>
            <DisableCounterpartyInsteadModal
                show={showDisableInsteadModal}
                counterparty={counterparty}
                mergeTargetName={
                    action === "merge"
                        ? counterpartyToMergeWith?.name
                        : undefined
                }
                onConfirm={onDisableInstead}
                onHide={() => setShowDisableInsteadModal(false)}
            />
            <CounterpartyLinkedToBillsOrInvoicesModal
                show={showBillOrInvoiceLinksModal}
                counterparty={counterparty}
                onConfirm={onArchiveInstead}
                onHide={() => setShowBillOrInvoiceLinksModal(false)}
            />
        </>
    );
};
